/**
 * CustomersPage — "Cliente 360": buscador unificado de clientes (email/teléfono/nombre)
 * y timeline de TODAS sus conversaciones abiertas, sin importar el canal
 * (Chat web, WhatsApp, Mensajes de Pedido / PrestaShop).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useOrganization } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  Search, Users, Mail, Phone, MessageSquare, Inbox, ExternalLink,
  Link2, Loader2, MailPlus, AlertTriangle, CheckCircle2,
} from 'lucide-react';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'ahora';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

function CustomerRow({ customer, selected, onClick }) {
  const label = customer.name || customer.emails?.[0] || customer.phones?.[0] || 'Cliente';
  return (
    <button
      type="button"
      data-testid={`customer-row-${customer.customer_id}`}
      onClick={onClick}
      className={`w-full text-left p-3 border-b border-gray-800/70 hover:bg-gray-800/40 transition-colors ${
        selected ? 'bg-gray-800/70 border-l-2 border-l-indigo-500' : ''
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-white truncate flex items-center gap-1.5">
          <Users size={13} className="text-indigo-400 flex-shrink-0" /> {label}
          {customer.multi_channel_open && (
            <span
              data-testid={`customer-multi-channel-badge-${customer.customer_id}`}
              title={`Contacto abierto a la vez en ${customer.open_channels?.length || 0} canales — evita respuestas duplicadas`}
              className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-400 text-[10px] font-medium flex-shrink-0"
            >
              <AlertTriangle size={9} /> multi-canal
            </span>
          )}
        </span>
        <span className="text-[11px] text-gray-500 flex-shrink-0">{timeAgo(customer.last_activity_at)}</span>
      </div>
      <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-gray-500">
        {customer.emails?.slice(0, 2).map(e => (
          <span key={e} className="flex items-center gap-1"><Mail size={10} /> {e}</span>
        ))}
        {customer.phones?.slice(0, 2).map(p => (
          <span key={p} className="flex items-center gap-1"><Phone size={10} /> {p}</span>
        ))}
        {customer.platform_refs?.length > 0 && (
          <span className="flex items-center gap-1"><Link2 size={10} /> {customer.platform_refs[0].platform}</span>
        )}
      </div>
    </button>
  );
}

function CustomerDetail({ customer, threadsData, loading }) {
  const navigate = useNavigate();
  if (!customer) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
        <div className="text-center">
          <Users size={32} className="mx-auto mb-2 text-gray-700" />
          Selecciona un cliente para ver su historial unificado
        </div>
      </div>
    );
  }

  const convs = threadsData?.conversations || [];
  const orders = threadsData?.order_threads || [];
  const emails = threadsData?.email_threads || [];

  return (
    <div className="flex-1 overflow-y-auto p-5" data-testid="customer-detail-panel">
      <h2 className="text-lg font-semibold text-white mb-1">
        {customer.name || customer.emails?.[0] || customer.phones?.[0] || 'Cliente'}
      </h2>
      <div className="flex flex-wrap gap-3 text-xs text-gray-400 mb-5">
        {customer.emails?.map(e => (
          <span key={e} className="flex items-center gap-1"><Mail size={11} /> {e}</span>
        ))}
        {customer.phones?.map(p => (
          <span key={p} className="flex items-center gap-1"><Phone size={11} /> {p}</span>
        ))}
        {customer.platform_refs?.map((r, i) => (
          <span key={i} className="flex items-center gap-1 text-indigo-400">
            <Link2 size={11} /> {r.platform} #{r.id_customer}
          </span>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 text-sm"><Loader2 size={14} className="animate-spin" /> Cargando historial...</div>
      ) : (
        <>
          <h3 className="text-xs font-semibold uppercase text-gray-500 mb-2">
            Conversaciones ({convs.length})
          </h3>
          <div className="space-y-2 mb-6">
            {convs.length === 0 && <p className="text-xs text-gray-600">Sin conversaciones de chat/WhatsApp.</p>}
            {convs.map(c => (
              <button
                key={c.conversation_id}
                data-testid={`customer-conv-${c.conversation_id}`}
                onClick={() => navigate(`/chat?conv=${c.conversation_id}`)}
                className="w-full flex items-center justify-between gap-2 p-2.5 rounded-lg bg-gray-800/50 border border-gray-800 hover:border-indigo-500/50 transition-colors text-left"
              >
                <span className="flex items-center gap-2 text-sm text-gray-200 min-w-0">
                  <MessageSquare size={13} className="text-indigo-400 flex-shrink-0" />
                  {c.type === 'whatsapp' ? 'WhatsApp' : 'Chat web'} · <span className="text-gray-500">{c.status}</span>
                  {c.answered_by_agent && <CheckCircle2 size={12} className="text-amber-400 flex-shrink-0" titleAccess="Respondido" />}
                  {c.last_message && <span className="text-gray-500 truncate max-w-[220px]">{c.last_message}</span>}
                </span>
                <span className="flex items-center gap-1 text-[11px] text-gray-500 flex-shrink-0">
                  {timeAgo(c.updated_at)} <ExternalLink size={11} />
                </span>
              </button>
            ))}
          </div>

          <h3 className="text-xs font-semibold uppercase text-gray-500 mb-2">
            Mensajes de Pedido ({orders.length})
          </h3>
          <div className="space-y-2">
            {orders.length === 0 && <p className="text-xs text-gray-600">Sin hilos de pedido.</p>}
            {orders.map(t => (
              <button
                key={t.thread_id}
                data-testid={`customer-order-${t.thread_id}`}
                onClick={() => navigate(`/order-messages?thread=${t.thread_id}`)}
                className="w-full flex items-center justify-between gap-2 p-2.5 rounded-lg bg-gray-800/50 border border-gray-800 hover:border-amber-500/50 transition-colors text-left"
              >
                <span className="flex items-center gap-2 text-sm text-gray-200 min-w-0">
                  <Inbox size={13} className="text-amber-400 flex-shrink-0" />
                  {t.external_order_id && t.external_order_id !== '0' ? `Pedido #${t.external_order_id}` : 'Sin pedido'}
                  {t.answered_by_agent && <CheckCircle2 size={12} className="text-amber-400 flex-shrink-0" titleAccess="Respondido" />}
                  <span className="text-gray-500 truncate max-w-[220px]">{t.last_message}</span>
                </span>
                <span className="flex items-center gap-1 text-[11px] text-gray-500 flex-shrink-0">
                  {timeAgo(t.updated_at)} <ExternalLink size={11} />
                </span>
              </button>
            ))}
          </div>

          <h3 className="text-xs font-semibold uppercase text-gray-500 mb-2">
            Emails de Soporte ({emails.length})
          </h3>
          <div className="space-y-2">
            {emails.length === 0 && <p className="text-xs text-gray-600">Sin emails de soporte.</p>}
            {emails.map(t => (
              <button
                key={t.thread_id}
                data-testid={`customer-email-${t.thread_id}`}
                onClick={() => navigate(`/support-email?thread=${t.thread_id}`)}
                className="w-full flex items-center justify-between gap-2 p-2.5 rounded-lg bg-gray-800/50 border border-gray-800 hover:border-emerald-500/50 transition-colors text-left"
              >
                <span className="flex items-center gap-2 text-sm text-gray-200 min-w-0">
                  <MailPlus size={13} className="text-emerald-400 flex-shrink-0" />
                  {t.subject || '(sin asunto)'}
                  {t.answered_by_agent && <CheckCircle2 size={12} className="text-amber-400 flex-shrink-0" titleAccess="Respondido" />}
                  <span className="text-gray-500 truncate max-w-[220px]">{t.last_message}</span>
                </span>
                <span className="flex items-center gap-1 text-[11px] text-gray-500 flex-shrink-0">
                  {timeAgo(t.updated_at)} <ExternalLink size={11} />
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function CustomersPage() {
  const { organization } = useOrganization();
  const orgId = organization?.id;

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [threadsData, setThreadsData] = useState(null);
  const [threadsLoading, setThreadsLoading] = useState(false);

  const loadCustomers = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const q = debouncedSearch.trim() ? `&search=${encodeURIComponent(debouncedSearch.trim())}` : '';
      const r = await api.get(`/api/customers?organization_id=${orgId}${q}`);
      setCustomers(r.data.customers || []);
    } catch { toast.error('Error al cargar clientes'); }
    finally { setLoading(false); }
  }, [orgId, debouncedSearch]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const selectCustomer = async (customer) => {
    setSelected(customer);
    setThreadsLoading(true);
    try {
      const r = await api.get(`/api/customers/${customer.customer_id}/threads?organization_id=${orgId}`);
      setThreadsData(r.data);
    } catch { toast.error('Error al cargar historial del cliente'); }
    finally { setThreadsLoading(false); }
  };

  return (
    <div className="h-screen overflow-hidden flex flex-col bg-gray-950" data-testid="customers-page">
      <div className="p-5 border-b border-gray-800 flex items-center justify-between flex-shrink-0">
        <div>
          <h1 className="text-xl font-semibold text-white flex items-center gap-2">
            <Users size={20} className="text-indigo-400" /> Clientes
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Vista unificada por email/teléfono: Chat, WhatsApp y Mensajes de Pedido de un mismo cliente en un solo sitio.
          </p>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div className="w-80 border-r border-gray-800 flex flex-col overflow-hidden flex-shrink-0">
          <div className="p-3 border-b border-gray-800">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-gray-500" />
              <input
                data-testid="customers-search-input"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Buscar por email, teléfono o nombre..."
                className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-8 pr-3 py-2 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-colors"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-xs text-gray-500 flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Cargando...</div>
            ) : customers.length === 0 ? (
              <div className="p-4 text-xs text-gray-600">Sin clientes vinculados todavía.</div>
            ) : (
              customers.map(c => (
                <CustomerRow
                  key={c.customer_id}
                  customer={c}
                  selected={selected?.customer_id === c.customer_id}
                  onClick={() => selectCustomer(c)}
                />
              ))
            )}
          </div>
        </div>

        <CustomerDetail customer={selected} threadsData={threadsData} loading={threadsLoading} />
      </div>
    </div>
  );
}
